/**
 * ModelSelector Component
 *
 * Dropdown selector for model management.
 * Part of the three-level selection system (Provider -> Pool -> Model).
 */

import React, { useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { StatusBadge } from '../shared/StatusBadge';
import { ConfirmDialog } from '../../ui/ConfirmDialog';

export interface ModelOption {
  model_use: string;
  model_name: string;
  model_type?: string;
  priority?: number;
  healthStatus: 'healthy' | 'unhealthy' | 'unknown';
  latency?: number;
}

export interface ModelSelectorProps {
  models: ModelOption[];
  selectedModel: string | null;
  onSelect: (modelUse: string) => void;
  onAdd: () => void;
  onDelete: (modelUse: string) => void;
  disabled?: boolean;
  className?: string;
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  models,
  selectedModel,
  onSelect,
  onAdd,
  onDelete,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const handleSelect = useCallback((modelUse: string) => {
    onSelect(modelUse);
    setIsOpen(false);
  }, [onSelect]);

  const handleDeleteClick = useCallback((e: React.MouseEvent, modelUse: string) => {
    e.stopPropagation();
    setDeleteTarget(modelUse);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteTarget) {
      onDelete(deleteTarget);
      setDeleteTarget(null);
    }
  }, [onDelete, deleteTarget]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const selectedOption = models.find(m => m.model_use === selectedModel);

  return (
    <div className={clsx('relative', className)}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        选择模型
      </label>
      
      {/* Dropdown Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          'w-full flex items-center justify-between px-4 py-3 border rounded-lg text-left transition-all',
          selectedOption ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-300',
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 cursor-pointer',
          isOpen && 'ring-2 ring-blue-500 border-blue-500'
        )}
      >
        <div className="flex items-center gap-3">
          <div className={clsx(
            'w-3 h-3 rounded-full',
            selectedOption?.healthStatus === 'healthy' ? 'bg-green-400' :
            selectedOption?.healthStatus === 'unhealthy' ? 'bg-red-400' : 'bg-gray-300'
          )} />
          <div>
            <div className="font-medium text-gray-900">
              {selectedOption?.model_use || '请选择模型'}
            </div>
            {selectedOption && (
              <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[200px]">
                {selectedOption.model_name}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedOption && (
            <StatusBadge 
              variant={selectedOption.healthStatus === 'healthy' ? 'success' : 
                      selectedOption.healthStatus === 'unhealthy' ? 'error' : 'neutral'} 
              size="sm"
            >
              {selectedOption.healthStatus === 'healthy' ? '正常' : 
               selectedOption.healthStatus === 'unhealthy' ? '异常' : '未知'}
            </StatusBadge>
          )}
          <svg
            className={clsx(
              'w-5 h-5 text-gray-400 transition-transform',
              isOpen && 'rotate-180'
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
            {/* Add Button */}
            <button
              type="button"
              onClick={() => {
                onAdd();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-blue-600 hover:bg-blue-50 border-b border-gray-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              添加新模型
            </button>

            {/* Model List */}
            {models.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                暂无模型配置
              </div>
            ) : (
              <div className="py-1">
                {models.map((model) => (
                  <div
                    key={model.model_use}
                    onClick={() => handleSelect(model.model_use)}
                    className={clsx(
                      'group flex items-center justify-between px-4 py-3 cursor-pointer transition-colors',
                      selectedModel === model.model_use ? 'bg-blue-50' : 'hover:bg-gray-50'
                    )}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={clsx(
                        'w-3 h-3 rounded-full flex-shrink-0',
                        model.healthStatus === 'healthy' ? 'bg-green-400' :
                        model.healthStatus === 'unhealthy' ? 'bg-red-400' : 'bg-gray-300'
                      )} />
                      <div className="min-w-0">
                        <div className={clsx(
                          'font-medium truncate',
                          selectedModel === model.model_use ? 'text-blue-700' : 'text-gray-900'
                        )}>
                          {model.model_use}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {model.model_name}
                        </div>
                      </div>
                    </div>
<div className="flex items-center gap-2 flex-shrink-0">
                       {model.model_type && (
                         <StatusBadge variant="info" size="sm">
                           {model.model_type}
                         </StatusBadge>
                       )}
                       {model.latency !== undefined && model.latency > 0 && (
                         <span className="text-xs text-gray-400">
                           {model.latency}ms
                         </span>
                       )}
                       <StatusBadge 
                         variant={model.healthStatus === 'healthy' ? 'success' : 
                                 model.healthStatus === 'unhealthy' ? 'error' : 'neutral'} 
                         size="sm"
                       >
                         {model.healthStatus === 'healthy' ? '正常' : 
                          model.healthStatus === 'unhealthy' ? '异常' : '未知'}
                       </StatusBadge>
                       <button
                        type="button"
                        onClick={(e) => handleDeleteClick(e, model.model_use)}
                        className="p-1 rounded transition-colors text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100"
                        title="删除模型"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="确认删除"
        message={`确定要删除模型「${deleteTarget || ''}」吗？此操作无法撤销。`}
        confirmLabel="删除"
        cancelLabel="取消"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};

export default ModelSelector;
